/**
 * Logger utility for consistent and configurable logging
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LoggerInterface {
  levels: Record<LogLevel, number>;
  currentLevel: number;
  debug: (component: string, message: string, data?: any) => void;
  info: (component: string, message: string, data?: any) => void;
  warn: (component: string, message: string, data?: any) => void;
  error: (component: string, message: string, data?: any) => void;
  startProcess: (processName: string) => void;
  endProcess: () => void;
  setLevel: (level: number) => void;
}

// Create a Logger utility object to centralize and control logging
export const Logger: LoggerInterface = {
  // Log levels
  levels: {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3
  },
  // Current log level - can be changed at runtime
  currentLevel: 0, // DEBUG by default
  
  // Main logging methods
  debug: (component: string, message: string, data?: any) => {
    if (Logger.currentLevel <= Logger.levels.DEBUG) {
      if (data) console.log(`${component}: ${message}`, data);
      else console.log(`${component}: ${message}`);
    }
  },
  
  info: (component: string, message: string, data?: any) => {
    if (Logger.currentLevel <= Logger.levels.INFO) {
      if (data) console.info(`${component}: ${message}`, data);
      else console.info(`${component}: ${message}`);
    }
  },
  
  warn: (component: string, message: string, data?: any) => {
    if (Logger.currentLevel <= Logger.levels.WARN) {
      if (data) console.warn(`${component}: ${message}`, data);
      else console.warn(`${component}: ${message}`);
    }
  },
  
  error: (component: string, message: string, data?: any) => {
    if (Logger.currentLevel <= Logger.levels.ERROR) {
      if (data) console.error(`${component}: ${message}`, data);
      else console.error(`${component}: ${message}`);
    }
  },
  
  // Group logs for processes
  startProcess: (processName: string) => {
    if (Logger.currentLevel <= Logger.levels.DEBUG) {
      console.group(`Process: ${processName}`);
    }
  },
  
  endProcess: () => {
    if (Logger.currentLevel <= Logger.levels.DEBUG) {
      console.groupEnd();
    }
  },
  
  // Set logging level
  setLevel: (level: number) => {
    Logger.currentLevel = level;
  }
};

// Set to INFO level by default for production builds
if (process.env.NODE_ENV === 'production') {
  Logger.setLevel(Logger.levels.INFO);
}

/**
 * Custom hook for using the Logger with a specific component name
 * 
 * @param componentName - The name of the component using the logger
 * @returns Object with logger methods pre-configured with the component name
 */
export const useLogger = (componentName: string) => {
  return {
    debug: (message: string, data?: any) => Logger.debug(componentName, message, data),
    info: (message: string, data?: any) => Logger.info(componentName, message, data),
    warn: (message: string, data?: any) => Logger.warn(componentName, message, data),
    error: (message: string, data?: any) => Logger.error(componentName, message, data),
    startProcess: Logger.startProcess,
    endProcess: Logger.endProcess
  };
};

export default Logger; 